'use client'
import { Box, Group, Skeleton, Text } from '@mantine/core'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { Suspense, useCallback, useMemo, useState } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import Swiper from '@/components/Swiper'
import useSubscribeLiveCoinsMarket from '@/hooks/business/useSubscribeLiveCoinsMarket'
import type { CoinItemInGetList } from '@/services/coin/coins'
import { useCoins, useTopMovers } from '@/services/coin/coins'
import type { BuzzingOnXItem, HotCoinsData } from '@/services/coin/market'
import { TrendingCardType } from '@/services/coin/market'
import type { CryptoEvent } from '@/services/coin/predictions'
import useGlobalStore from '@/stores/global'
import type { CoinSlug, LiveCoinMarket, TopMoverItem } from '@/types/coin'

import PredictionCard from './PredictionCard'
import TrendingCard from './TrendingCard'

function TrendingFallback() {
  return (
    <Box p={16}>
      <Skeleton
        height={32}
        w={160}
        radius="xl"
        mb="sm"
        style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
      />
      <Group gap="md" wrap="nowrap">
        {[0, 1, 2, 3].map(index => (
          <Skeleton
            key={index}
            h={{ base: 270, md: 274 }}
            w={{ base: 253, md: 230, lg: 302 }}
            miw={{ base: 253, md: 230, lg: 302 }}
            style={{
              backgroundColor: 'var(--bg-surface-secondary)',
              flexShrink: 0,
              borderRadius: 32,
            }}
          />
        ))}
      </Group>
    </Box>
  )
}

type SwiperItem =
  | { kind: 'trending'; type: TrendingCardType; data?: Array<{ name: string }> }
  | { kind: 'prediction'; event: CryptoEvent }

interface TrendingContentProps {
  buzzingOnXData?: BuzzingOnXItem[]
  hotCoinsData?: HotCoinsData
  newCoinsData?: CoinItemInGetList[]
  topMoversData?: TopMoverItem[]
  predictionEvents?: CryptoEvent[]
  isUS?: boolean
}

function TrendingContent({
  buzzingOnXData,
  hotCoinsData,
  newCoinsData,
  topMoversData,
  predictionEvents,
  isUS,
  liveCache,
}: TrendingContentProps & { liveCache: Record<CoinSlug, LiveCoinMarket> }) {
  const t = useTranslations('trending')
  const { locale } = useParams<{ locale: string }>()

  const swiperList = useMemo((): SwiperItem[] => {
    const base: SwiperItem[] = [
      { kind: 'trending', data: hotCoinsData, type: TrendingCardType.HotTokens },
      { kind: 'trending', data: buzzingOnXData, type: TrendingCardType.BuzzingX },
    ]

    if (isUS) {
      const predItems: SwiperItem[] = (predictionEvents ?? []).map(event => ({
        kind: 'prediction',
        event,
      }))
      return [...predItems, ...base]
    }

    return [
      ...base,
      { kind: 'trending', data: topMoversData, type: TrendingCardType.TopMovers },
      { kind: 'trending', data: newCoinsData, type: TrendingCardType.NewlyListed },
    ]
  }, [isUS, buzzingOnXData, topMoversData, hotCoinsData, newCoinsData, predictionEvents])

  if (!swiperList || swiperList.length === 0) return null

  return (
    <>
      <Swiper
        title={t('title_2')}
        swiperList={swiperList}
        renderSwiper={(item, idx) => {
          if (item.kind === 'prediction') {
            return <PredictionCard key={idx} event={item.event} />
          }
          return <TrendingCard type={item.type} key={idx} data={item.data} liveCache={liveCache} />
        }}
      />
      {locale === 'uk' && (
        <Box px={2} py={12}>
          <Text size="11px" fw={500} lh="16px" c="#7b849b">
            Past Performance is not a reliable indicator of future performance. Figures shown are
            based on gross performance and fees may reduce your net returns. Source: crypto.com
          </Text>
        </Box>
      )}
    </>
  )
}

interface TrendingProps {
  buzzingOnXData?: BuzzingOnXItem[]
  hotCoinsData?: HotCoinsData
  predictionEvents?: CryptoEvent[]
  isUS: boolean
}

function Trending({ buzzingOnXData, hotCoinsData, predictionEvents, isUS }: TrendingProps) {
  const { data: topMoversData } = useTopMovers({ depth: 20 })
  const { data: newCoins } = useCoins({
    page: 1,
    limit: 100,
    sort: 'createTime',
    direction: 'DESC',
  })
  const newCoinsData = useMemo(() => newCoins?.data ?? [], [newCoins?.data])
  const allCoins = useGlobalStore(state => state.allCoins)

  // Top-level subscription for all trending slugs
  const [liveCache, setLiveCache] = useState<Record<CoinSlug, LiveCoinMarket>>({})
  const updateLiveCoinsMarket = useCallback((next: Record<CoinSlug, LiveCoinMarket>) => {
    setLiveCache(next)
  }, [])
  const clearLiveCoinsMarket = useCallback(() => {
    setLiveCache({})
  }, [])
  const combinedSlugs = useMemo(() => {
    const slugs = new Set<CoinSlug>()
    hotCoinsData?.forEach(c => c?.slug && slugs.add(c.slug as CoinSlug))
    topMoversData?.forEach(c => c?.slug && slugs.add(c.slug as CoinSlug))
    newCoinsData?.forEach(c => c?.slug && slugs.add(c.slug as CoinSlug))
    buzzingOnXData?.forEach(c => {
      const name = c?.name.trim().toLowerCase()
      if (!name) return
      const matched = allCoins.find(coin => coin.name.trim().toLowerCase() === name)
      if (matched?.slug) slugs.add(matched.slug as CoinSlug)
    })
    return Array.from(slugs)
  }, [hotCoinsData, topMoversData, newCoinsData, buzzingOnXData, allCoins])
  useSubscribeLiveCoinsMarket(combinedSlugs, { updateLiveCoinsMarket, clearLiveCoinsMarket }, 1000)

  return (
    <Suspense fallback={<TrendingFallback />}>
      <TrendingContent
        buzzingOnXData={buzzingOnXData}
        topMoversData={topMoversData}
        hotCoinsData={hotCoinsData}
        newCoinsData={newCoinsData}
        predictionEvents={predictionEvents}
        isUS={isUS}
        liveCache={liveCache}
      />
      <Box id="trending-bottom"></Box>
    </Suspense>
  )
}

export default SectionErrorBoundary.with(Trending)
